import { Box, Button, Flex, Icon, ListItem, Stack, Text, UnorderedList, useToast } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaPlayCircle, FaTimesCircle } from "react-icons/fa";
import { type OptionDto, useUpdateContractMutation } from "../../../../autogen/bff-api";
import { isApiErrorDto } from "../../../../common/errors/isApiErrorDto";
import { isDisplayableDate } from "../../../../common/formatting/displayDate";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { ContractState } from "../../../../common/redux/reducers/contractReducer";
import { fetchSettledContract } from "../../../../common/redux/thunks/contract/load-contract-thunk";
import { isRefusedAsAlreadyDecided } from "../../../../common/redux/thunks/contract/optionDecidedElsewhere";
import { ConfirmationNotCompleted, useConfirmationModal } from "../../../../common/useConfirmationModal";
import { useAlreadyDecidedMessage } from "../../options/alreadyDecided";
import { useExtensionName } from "../../options/extensionName";
import { ExtensionSummary, ExtensionTimeline } from "../../options/ExtensionTimeline";
import { daysFromToday, inPeriodOrder, isDecided, lapsingWith } from "../../options/extensionStatus";
import { endsAfterLastSupportedDate, LAST_SUPPORTED_DATE } from "../../options/optionKind";
import {
  OptionDeadlinePassed,
  OptionDuration,
  OptionLapsedBecause,
  OptionPeriod,
  OptionStatus,
} from "../../options/OptionPeriod";
import { requireDataFields } from "../../typeguards";
import { GenericDetails } from "./GenericDetails";

const isRefusedAsPastLastSupportedDate = (error: unknown): boolean => {
  const body = (error as { data?: unknown } | null | undefined)?.data;
  return isApiErrorDto(body) && body.errorCode === "zz_FieldErrorCode_EndDateAfterLastSupportedDate";
};

interface Props {
  data: ContractState["contract"];
  today?: Date;
}

export const Options = ({ data, today: givenToday }: Props) => {
  const { t } = useTranslation();
  const dataFields = requireDataFields(data);
  const today = useMemo(() => givenToday ?? new Date(), [givenToday]);
  const options = dataFields.options ?? [];
  const endDate = dataFields.endDate?.date;
  const value =
    options.length === 0 ? (
      <NoExtensions canDecide={data.canEditEverything} />
    ) : (
      <Stack pt="2" spacing="4" width="full">
        <ExtensionSummary options={options} endDate={endDate} today={today} />
        <ExtensionTimeline options={options} startDate={dataFields.startDate} endDate={endDate} today={today} />
        <ExtensionDecisions
          contractId={data.id}
          options={options}
          endDate={endDate}
          canDecide={data.canEditEverything}
          today={today}
        />
      </Stack>
    );

  return (
    <GenericDetails
      title={t("Optional contract extensions")}
      rows={[{ type: "Data", name: "", displayBelow: true, value }]}
    />
  );
};

const NoExtensions = ({ canDecide }: { canDecide: boolean }) => {
  const { t } = useTranslation();
  return (
    <Text pt="2" fontSize="sm" color="smMuted" data-testid="no-extensions">
      {t("No extensions are registered on this contract.")}
      {canDecide && ` ${t("Extensions are added when editing the contract.")}`}
    </Text>
  );
};

interface DecisionsProps {
  contractId: string;
  options: OptionDto[];
  endDate?: string | null;
  canDecide: boolean;
  today: Date;
}

const ExtensionDecisions = ({ contractId, options, endDate, canDecide, today }: DecisionsProps) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const toast = useToast();
  const { show: confirm, modal: confirmationModal } = useConfirmationModal();
  const [updateContract] = useUpdateContractMutation();
  const nameOf = useExtensionName(options);
  const dispatch = useAppDispatch();
  const alreadyDecided = useAlreadyDecidedMessage(options);

  const save = async (
    request: Parameters<typeof updateContract>[0]["updateContractRequest"]
  ): Promise<"saved" | "alreadyDecided" | "pastLastSupportedDate" | "failed"> => {
    try {
      await updateContract({ contractId, updateContractRequest: request }).unwrap();
      return "saved";
    } catch (e) {
      if (isRefusedAsAlreadyDecided(e)) return "alreadyDecided";
      return isRefusedAsPastLastSupportedDate(e) ? "pastLastSupportedDate" : "failed";
    }
  };

  const whyNotExercisable = (option: OptionDto): string =>
    option.datesSetByHand
      ? t(
          "This extension ends after {{date}}, the last date a contract can run to, so it cannot be exercised as it stands. Change its dates when editing the contract, so that it ends by {{date}}.",
          { date: displayDate(LAST_SUPPORTED_DATE) }
        )
      : t(
          "This extension ends after {{date}}, the last date a contract can run to, so it cannot be exercised as it stands. Move «End date» earlier when editing the contract, so that the extension ends by {{date}}.",
          { date: displayDate(LAST_SUPPORTED_DATE) }
        );

  const sayAlreadyDecided = async (option: OptionDto) => {
    const decided = await decisionOf(option);
    toast({
      title: decided
        ? alreadyDecided(decided)
        : t("{{name}} is already decided. Reload the page to see the decision.", { name: nameOf(option) }),
      status: "info",
    });
  };

  const decisionOf = async (option: OptionDto): Promise<OptionDto | undefined> => {
    try {
      const fresh = await dispatch(fetchSettledContract({ contractId })).unwrap();
      return fresh.dataFields?.options?.find((o) => o.id === option.id);
    } catch {
      return undefined;
    }
  };

  const keepOpenWithError = (title: string): never => {
    toast({ title, status: "error" });
    throw new ConfirmationNotCompleted(title);
  };

  const confirmExercise = (option: OptionDto) => {
    const deadlinePassed = !!option.deadline && daysFromToday(option.deadline, today) < 0;
    confirm({
      title: String(t("Exercise {{name}}?", { name: nameOf(option) })),
      description: (
        <Stack spacing="2">
          {endsAfterLastSupportedDate(option) ? (
            <Text fontWeight="semibold" data-testid="exercise-past-last-supported-date">
              {whyNotExercisable(option)}
            </Text>
          ) : (
            <Text>{t("The new end date will be {{date}}.", { date: displayDate(option.endDate ?? "") })}</Text>
          )}
          {deadlinePassed ? (
            <Text fontWeight="semibold" data-testid="exercise-deadline-passed">
              {t("The deadline to exercise passed on {{date}}. Remember to notify the supplier in writing.", {
                date: displayDate(option.deadline ?? ""),
              })}
            </Text>
          ) : option.deadline ? (
            <Text>
              {t("Remember to notify the supplier in writing by {{date}}.", { date: displayDate(option.deadline) })}
            </Text>
          ) : (
            <Text>{t("Remember to notify the supplier in writing.")}</Text>
          )}
          <Text>{t("This cannot be undone.")}</Text>
        </Stack>
      ),
      cancelButtonText: String(t("Cancel")),
      confirmButtonText: String(t("Exercise extension")),
      onConfirmed: async () => {
        if (!option.id) return;
        const outcome = await save({ executeOption: { id: option.id } });
        if (outcome === "alreadyDecided") return sayAlreadyDecided(option);
        if (outcome === "pastLastSupportedDate") keepOpenWithError(whyNotExercisable(option));
        if (outcome === "failed") {
          keepOpenWithError(t("The option was not exercised. Reload the page and try again."));
        }
        toast({ title: t("{{name}} is exercised", { name: nameOf(option) }), status: "success" });
      },
    });
  };

  const confirmDecline = (option: OptionDto) => {
    const lapsing = lapsingWith(options, option);
    confirm({
      title: String(t("Not exercise {{name}}?", { name: nameOf(option) })),
      description: (
        <Stack spacing="2">
          <Text>
            {endDate
              ? t("The contract then ends on {{date}}, as it does today.", { date: displayDate(endDate) })
              : t("The contract's end date stays as it is.")}
          </Text>
          <Text>{t("This lapses:")}</Text>
          <UnorderedList data-testid="decline-lapses">
            {lapsing.map((lapsed) => (
              <ListItem key={lapsed.id}>
                {lapsed.startDate &&
                lapsed.endDate &&
                isDisplayableDate(lapsed.startDate) &&
                isDisplayableDate(lapsed.endDate)
                  ? `${nameOf(lapsed)} (${displayDate(lapsed.startDate)} – ${displayDate(lapsed.endDate)})`
                  : nameOf(lapsed)}
              </ListItem>
            ))}
          </UnorderedList>
          <Text>
            {t("You will get no more reminders about the deadline. If the supplier should know, you must tell them yourself.")}
          </Text>
          <Text>{t("This cannot be undone.")}</Text>
        </Stack>
      ),
      cancelButtonText: String(t("Cancel")),
      confirmButtonText: String(t("Do not exercise")),
      confirmButtonColor: "red",
      onConfirmed: async () => {
        if (!option.id) return;
        const outcome = await save({ declineOption: { id: option.id } });
        if (outcome === "alreadyDecided") return sayAlreadyDecided(option);
        if (outcome === "failed") {
          keepOpenWithError(t("Your choice not to exercise the option was not saved. Reload the page and try again."));
        }
        const othersLapsed = lapsing.length - 1;
        toast({
          title:
            othersLapsed > 0
              ? t("{{name}} will not be exercised. {{count}} other extensions lapsed.", {
                  name: nameOf(option),
                  count: othersLapsed,
                })
              : t("{{name}} will not be exercised.", { name: nameOf(option) }),
          status: "success",
        });
      },
    });
  };

  return (
    <Stack spacing="3">
      {confirmationModal}
      {inPeriodOrder(options).map((option) => {
        const waitsFor = options.find((other) => other.id === option.waitsForOptionId);
        const canExercise = !waitsFor && !!option.endDate && !option.doesNotExtendContract;
        return (
          <Box
            key={option.id}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            p="3"
            data-testid={`extension-${option.id}`}
          >
            <Flex justifyContent="space-between" alignItems="flex-start" gap="2" wrap="wrap">
              <Text fontWeight="bold" fontSize="sm">
                {nameOf(option)}
              </Text>
              <OptionStatus option={option} today={today} />
            </Flex>
            {option.description && (
              <Box as="pre" fontFamily="body" whiteSpace="pre-wrap" fontSize="sm" pt="1">
                {option.description}
              </Box>
            )}
            <Box pt="1">
              <OptionDuration option={option} />
              <OptionPeriod option={option} />
            </Box>
            <OptionLapsedBecause option={option} options={options} />
            <OptionDeadlinePassed option={option} today={today} />
            {canDecide && !isDecided(option) && (
              <Box pt="2">
                <Flex gap="2" wrap="wrap">
                  <Button
                    size="sm"
                    colorScheme="teal"
                    rightIcon={<Icon as={FaPlayCircle} />}
                    isDisabled={!canExercise}
                    onClick={() => confirmExercise(option)}
                  >
                    {t("Exercise")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    rightIcon={<Icon as={FaTimesCircle} />}
                    onClick={() => confirmDecline(option)}
                  >
                    {t("Do not exercise")}
                  </Button>
                </Flex>
                {waitsFor ? (
                  <Text fontSize="sm" color="smMuted" pt="1" data-testid="extension-waits-for">
                    {t("«Exercise» becomes available once {{name}} is decided. Extensions are exercised in order.", { name: nameOf(waitsFor) })}
                  </Text>
                ) : !option.endDate ? (
                  <Text fontSize="sm" color="smMuted" pt="1" data-testid="extension-without-dates">
                    {option.extension && !endDate
                      ? t("Set the contract's end date to calculate the dates, or set the dates yourself.")
                      : t("This extension has no end date yet. Give it a length or dates when editing the contract.")}
                  </Text>
                ) : option.doesNotExtendContract ? (
                  <Text fontSize="sm" color="smMuted" pt="1" data-testid="extension-does-not-extend">
                    {t(
                      "This extension ends on {{end}}, which is not after the contract's end date {{contractEnd}}. Change its dates when editing the contract.",
                      { end: displayDate(option.endDate), contractEnd: displayDate(endDate ?? "") }
                    )}
                  </Text>
                ) : (
                  endsAfterLastSupportedDate(option) && (
                    <Text fontSize="sm" color="smMuted" pt="1" data-testid="extension-past-last-supported-date">
                      {whyNotExercisable(option)}
                    </Text>
                  )
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
